import { Button } from "@/components/ui/Button";
import { Tooltip } from "@/components/ui/Tooltip";
import { SharePanel } from "@/features/clipboard/components/SharePanel";
import { clipboardFolderUrl } from "@/features/clipboard/utils/urls";
import { shareExtractionToClipboard } from "@/features/data-curation/api/curation";
import type { ExtractionShare } from "@/features/data-curation/types";
import { Check, Copy, Share2 } from "lucide-react";
import { useState } from "react";

interface DatasetShareButtonProps {
  datasetId: string;
  datasetName: string;
  /** Classes of the "Share" label, which hide it when only the icon shows. */
  labelClassName?: string;
}

/** Copies the dataset to a clipboard folder, then opens the clipboard share dialog for it. */
export const DatasetShareButton = ({
  datasetId,
  datasetName,
  labelClassName = "sr-only @4xl:not-sr-only",
}: DatasetShareButtonProps) => {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [share, setShare] = useState<ExtractionShare | null>(null);
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    setPending(true);
    setError(null);
    try {
      setShare(await shareExtractionToClipboard(datasetId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not share the dataset");
    } finally {
      setPending(false);
    }
  };

  const link = share
    ? `${window.location.origin}${clipboardFolderUrl(share.folder_path, share.owner_id)}`
    : "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Could not copy the link");
    }
  };

  const fileSummary = share
    ? [
        `${share.rows} ${share.rows === 1 ? "row" : "rows"}`,
        share.images > 0 ? `${share.images} ${share.images === 1 ? "image" : "images"}` : null,
      ]
        .filter(Boolean)
        .join(" and ")
    : "";

  return (
    <>
      <Tooltip content="Copy a read-only snapshot of this dataset to your clipboard and share it.">
        <Button
          size="sm"
          variant="outline"
          loading={pending}
          aria-label={pending ? "Sharing dataset" : undefined}
          onClick={() => void handleShare()}
        >
          <Share2 className="h-4 w-4" aria-hidden />
          <span className={labelClassName}>Share</span>
        </Button>
      </Tooltip>
      {error && !share && (
        <span role="alert" className="text-xs text-red-700">
          {error}
        </span>
      )}
      {share && (
        <SharePanel
          folderPath={share.folder_path}
          title={datasetName}
          onClose={() => {
            setShare(null);
            setError(null);
          }}
          intro={
            <div className="mb-4 space-y-3">
              <p className="text-sm text-zinc-600">
                A read-only snapshot ({fileSummary}) is in your clipboard folder{" "}
                <span className="font-medium text-zinc-900">
                  {share.folder_path}
                </span>
                . Invite people by email, then send them the link.
              </p>
              {share.image_note && (
                <p className="text-xs text-amber-700">
                  Images: {share.image_note}
                </p>
              )}
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={link}
                  aria-label="Share link"
                  className="min-w-0 flex-1 rounded-[3px] border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-xs text-zinc-700"
                  onFocus={(event) => event.currentTarget.select()}
                />
                <Button size="sm" variant="outline" onClick={() => void handleCopy()}>
                  {copied ? (
                    <Check className="h-4 w-4" aria-hidden />
                  ) : (
                    <Copy className="h-4 w-4" aria-hidden />
                  )}
                  {copied ? "Copied" : "Copy link"}
                </Button>
              </div>
              {error && (
                <p role="alert" className="text-xs text-red-700">
                  {error}
                </p>
              )}
            </div>
          }
          footer={
            <p className="mt-4 text-xs text-zinc-500">
              The snapshot does not change when the dataset changes. Click Share again to update
              it. People you invited keep access.
            </p>
          }
        />
      )}
    </>
  );
};
